// orderBlockEngine.js
// Shared order block source of truth for the pipeline and overlay.

export const ORDER_BLOCK_STATES = {
  FRESH: "FRESH",
  MITIGATED: "MITIGATED",
};

function normalizeMarket(market) {
  return String(market || "").toUpperCase();
}

function getAnalyzedCandles(candles) {
  if (!Array.isArray(candles) || candles.length === 0) {
    return [];
  }

  const lastCandle = candles[candles.length - 1];

  if (lastCandle?.isClosed === false) {
    return candles.slice(0, -1);
  }

  return candles;
}

function overlapsOrderBlock(candle, orderBlock) {
  return candle.low <= orderBlock.high && candle.high >= orderBlock.low;
}

function buildOrderBlockShape({
  market,
  obCandle,
  obIndex,
  breakoutCandle,
  breakoutIndex,
  reason,
  refinement,
  source,
}) {
  const normalizedMarket = normalizeMarket(market);
  const high = Math.max(obCandle.open, obCandle.close);
  const low = Math.min(obCandle.open, obCandle.close);
  const type = normalizedMarket === "BOOM" ? "demand_ob" : "supply_ob";

  return {
    exists: true,
    valid: true,
    id: `ob-${type}-${obCandle.time}`,
    name: normalizedMarket === "BOOM" ? "Demand OB" : "Supply OB",
    zoneName: type,
    type,
    isDemand: normalizedMarket === "BOOM",
    high,
    low,
    range: high - low,
    state: ORDER_BLOCK_STATES.FRESH,
    mitigated: false,
    tested: false,
    startTime: obCandle.time,
    breakoutTime: breakoutCandle.time,
    originTime: obCandle.time,
    originIndex: obIndex,
    obIndex,
    bosIndex: breakoutIndex,
    bosTime: breakoutCandle.time,
    timestamp: obCandle.time,
    source,
    ...refinement,
    reason,
  };
}

function buildFvgRefinement(candles, obIndex, bosIndex, market) {
  const leftCandle = candles[obIndex - 1];
  const bosCandle = candles[bosIndex];
  const normalizedMarket = normalizeMarket(market);

  if (!leftCandle || !bosCandle) {
    return {
      hasFvgRefinement: false,
      fvgStartTime: null,
      fvgEndTime: null,
    };
  }

  const hasBullishGap =
    normalizedMarket === "BOOM" && bosCandle.low > leftCandle.high;
  const hasBearishGap =
    normalizedMarket === "CRASH" && bosCandle.high < leftCandle.low;

  if (!hasBullishGap && !hasBearishGap) {
    return {
      hasFvgRefinement: false,
      fvgStartTime: null,
      fvgEndTime: null,
    };
  }

  return {
    hasFvgRefinement: true,
    fvgStartTime: leftCandle.time,
    fvgEndTime: bosCandle.time,
  };
}

export function detectOrderBlock({
  candles,
  market,
  bosIndex,
  options = {},
}) {
  const analyzedCandles = getAnalyzedCandles(candles);
  const normalizedMarket = normalizeMarket(market);

  if (bosIndex == null || bosIndex < 1 || bosIndex >= analyzedCandles.length) {
    return { exists: false, reason: "Invalid BOS index" };
  }

  const obIndex = bosIndex - 1;
  const obCandle = analyzedCandles[obIndex];
  const bosCandle = analyzedCandles[bosIndex];

  if (!obCandle || !bosCandle) {
    return { exists: false, reason: "Missing order block candles" };
  }

  const isBullish = obCandle.close > obCandle.open;
  const isBearish = obCandle.close < obCandle.open;

  if (normalizedMarket === "BOOM" && !isBearish) {
    return { exists: false, reason: "OB candle not bearish" };
  }

  if (normalizedMarket === "CRASH" && !isBullish) {
    return { exists: false, reason: "OB candle not bullish" };
  }

  const refinement =
    options.refineWithFvg === false
      ? {
          hasFvgRefinement: false,
          fvgStartTime: null,
          fvgEndTime: null,
        }
      : buildFvgRefinement(analyzedCandles, obIndex, bosIndex, normalizedMarket);

  return buildOrderBlockShape({
    market: normalizedMarket,
    obCandle,
    obIndex,
    breakoutCandle: bosCandle,
    breakoutIndex: bosIndex,
    source: "BOS",
    refinement,
    reason: refinement.hasFvgRefinement
      ? "Valid BOS order block with FVG refinement"
      : "Valid BOS order block detected",
  });
}

export function detectFallbackOrderBlock(candles, market, options = {}) {
  const analyzedCandles = getAnalyzedCandles(candles);
  const normalizedMarket = normalizeMarket(market);
  const maxLookback =
    options.maxLookback && options.maxLookback > 0
      ? options.maxLookback
      : analyzedCandles.length - 2;

  if (analyzedCandles.length < 3) {
    return null;
  }

  const startIndex = Math.max(1, analyzedCandles.length - maxLookback - 1);

  for (let middleIndex = analyzedCandles.length - 2; middleIndex >= startIndex; middleIndex -= 1) {
    const leftCandle = analyzedCandles[middleIndex - 1];
    const middleCandle = analyzedCandles[middleIndex];
    const rightCandle = analyzedCandles[middleIndex + 1];

    if (!leftCandle || !middleCandle || !rightCandle) {
      continue;
    }

    const isDemandCandidate =
      normalizedMarket === "BOOM" &&
      middleCandle.close < middleCandle.open &&
      rightCandle.low > leftCandle.high;
    const isSupplyCandidate =
      normalizedMarket === "CRASH" &&
      middleCandle.close > middleCandle.open &&
      rightCandle.high < leftCandle.low;

    if (!isDemandCandidate && !isSupplyCandidate) {
      continue;
    }

    const fallbackOrderBlock = buildOrderBlockShape({
      market: normalizedMarket,
      obCandle: middleCandle,
      obIndex: middleIndex,
      breakoutCandle: rightCandle,
      breakoutIndex: middleIndex + 1,
      source: "FVG_FALLBACK",
      refinement: {
        hasFvgRefinement: true,
        fvgStartTime: leftCandle.time,
        fvgEndTime: rightCandle.time,
      },
      reason: "Fallback order block candidate detected without BOS",
    });

    return updateOrderBlockState({
      candles: analyzedCandles,
      orderBlock: fallbackOrderBlock,
      bosIndex: middleIndex + 1,
    });
  }

  return null;
}

export function updateOrderBlockState({
  candles,
  orderBlock,
  bosIndex,
}) {
  if (!orderBlock?.exists) {
    return null;
  }

  const analyzedCandles = getAnalyzedCandles(candles);
  const mitigationStartIndex = Math.max(
    (bosIndex ?? orderBlock.bosIndex ?? 0) + 1,
    0
  );

  for (
    let candleIndex = mitigationStartIndex;
    candleIndex < analyzedCandles.length;
    candleIndex += 1
  ) {
    const candle = analyzedCandles[candleIndex];

    if (!overlapsOrderBlock(candle, orderBlock)) {
      continue;
    }

    return {
      ...orderBlock,
      state: ORDER_BLOCK_STATES.MITIGATED,
      mitigated: true,
      tested: true,
      testTime: candle.time,
      mitigateIndex: candleIndex,
      reason: "Order block touched by price",
    };
  }

  return {
    ...orderBlock,
    state: ORDER_BLOCK_STATES.FRESH,
    mitigated: false,
    tested: false,
    testTime: null,
    mitigateIndex: null,
    reason: "Order block untouched",
  };
}
